import React, { useEffect, useState } from "react";
import { BrowserView, MobileView } from "react-device-detect";
import T from "src/ipm-shared/Utils/Intl";

import { history } from "src/ipm-shared/store";
import {
  FORM,
  INTERNATIONAL_FORM
} from "src/ipm-shared/store/model/Payee/const";
import _get from "lodash-es/get";
import _isEmpty from "lodash-es/isEmpty";

import {
  ADD_LANDLORD_FIRST_SCREEN_ERROR_FIELD,
  ADD_SUPPLIER_FIRST_SCREEN_ERROR_FIELD,
  ADD_SECOND_SCREEN_ERROR_FIELD
} from "../PaymentsCreationModal/const";

import ROUTES from "src/bepaid/routes";
import Button from "src/bepaid/components/Form/controls/Button";
import DesktopModalLayout from "src/bepaid/components/Modal/components/DesktopModalLayout";
import { invokeMessage } from "src/bepaid/components/Message";
import MainLayout from "src/bepaid/layouts/MainLayout";
import { getEntityByType, typeByEntity } from "src/bepaid/utils/recipient";

import RecipientLandlordForm from "./components/RecipientLandlordForm";
import RecipientEmployeeForm from "./components/RecipientEmployeeForm";
import RecipientSupplierForm from "./components/RecipientSupplierForm";
import RecipientBankAccountForm from "./components/RecipientBankAccountForm";

import styles from "./RecipientModal.module.scss";
import FeatureGateUtil from "src/ipm-shared/components/FeatureGating/Util";

const View = (props: any) => {
  const [fetching, setFetching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [payee, setPayee] = useState(null);

  const purpose = getEntityByType(props.extraInfo.type);
  const payeeId = parseInt(props.extraInfo.payeeId);

  const disabled =
    purpose === "rent"
      ? (!props.controls["confirm_tenancy_agreement"] ||
          props.controls["confirm_tenancy_agreement"]?.value) &&
        (!props.controls["rent_confirm_tenancy_agreement_business"] ||
          props.controls["rent_confirm_tenancy_agreement_business"]?.value)
        ? false
        : true
      : !props.controls["confirm_tenancy_agreement"] ||
        props.controls["confirm_tenancy_agreement"]?.value
      ? false
      : true;

  const modalInfo = ({
    employee: {
      title: "Add employee",
      titleLoading: "Adding employee...",
      editModeTitle: "Employee details",
      editModeSubmitTitle: "Save changes",
      editModeTitleLoading: "Saving changes...",
      urlBack: ROUTES.RECIPIENTS_EMPLOYEES
    },
    landlord: {
      title: "Add landlord",
      titleLoading: "Adding landlord...",
      editModeTitle: "Landlord details",
      editModeSubmitTitle: "Save changes",
      editModeTitleLoading: "Saving changes...",
      urlBack: ROUTES.RECIPIENTS_LANDLORDS
    },
    supplier: {
      title: "Add supplier",
      titleLoading: "Adding supplier...",
      editModeTitle: "Supplier details",
      editModeSubmitTitle: "Save changes",
      editModeTitleLoading: "Saving changes...",
      urlBack: ROUTES.RECIPIENTS_SUPPLIERS
    },
    bank_account: {
      title: "Add bank accounts",
      titleLoading: "Adding bank accounts...",
      editModeTitle: "Bank accounts details",
      editModeSubmitTitle: "Save changes",
      editModeTitleLoading: "Saving changes...",
      urlBack: ROUTES.RECIPIENTS_BANK_ACCOUNTS
    }
  } as Record<string, any>)[props.extraInfo.type];

  const toggleModal = () => {
    history.push(modalInfo.urlBack);
  };

  const isAllowedSupplierUENFlow =
    FeatureGateUtil.verifyFeature(props.features, "PAY_TO_UEN_FLOW") &&
    props.extraInfo.type === typeByEntity.suppliers &&
    !props.editMode;

  const toggleModalUpdate = (onClose?: boolean) => {
    props.removeForm(
      [typeByEntity.suppliers, typeByEntity.landlords].includes(
        props.extraInfo.type
      )
        ? FORM
        : INTERNATIONAL_FORM
    );
    if (props.editMode && !onClose) {
      fetchPayees();
    }
    if (isAllowedSupplierUENFlow) {
      toggleModal();
      return;
    }
    const returnTo = new URLSearchParams(window.location.search).get(
      "return_to"
    );
    if (returnTo) {
      history.push(decodeURIComponent(returnTo));
    } else {
      history.goBack();
    }
  };

  const onBack = () => {
    history.goBack();
  };

  const fetchPayees = () => {
    let ids: any[] = [];
    const isInternational = _get(payee, "is_international");
    props.fetchPayees({
      type: purpose,
      getAllPayees: true,
      noRedirect: true,
      pageCount: 1000,
      cb: (payees: any) => {
        setPayee(payees.find((payee: any) => +payee.id === +payeeId));
      },
      loadingFunc: setFetching
    });
    if (!props.selectedPayee.includes(payeeId)) {
      if (isInternational) {
        ids = [payeeId];
        props.selectPayees({
          ids,
          purpose,
          removedIds: [...props.selectedPayee].filter(
            (id: any) => +id !== +payeeId
          )
        });
      } else {
        ids = [...props.selectedPayee, payeeId];
        props.selectPayees({
          ids,
          purpose,
          removedIds: []
        });
      }
    }
  };

  const formName = [
    typeByEntity.suppliers,
    typeByEntity.landlords,
    typeByEntity.employees
  ].includes(props.extraInfo.type)
    ? FORM
    : INTERNATIONAL_FORM;

  // Controls declared with reserveValueOnUnmount stay in the store after their screen is
  // unmounted, and getControls() returns every control in the store regardless of form.
  // Failing on one of those attaches a required-field error to a field that is not on this
  // page: the user sees nothing and no request is ever sent. Only fields this form actually
  // renders can fail here - everything else is left to the API to validate.
  const isFieldRendered = (field: string) =>
    document.getElementsByName(field).length > 0;

  const validateField = () => {
    const errors: any = {
      fields: {},
      form: []
    };

    let fieldsToValidate: string[] = [];

    if (props.extraInfo.type === typeByEntity.suppliers) {
      fieldsToValidate = [
        ...ADD_SUPPLIER_FIRST_SCREEN_ERROR_FIELD,
        ...ADD_SECOND_SCREEN_ERROR_FIELD
      ];
    } else if (props.extraInfo.type === typeByEntity.landlords) {
      fieldsToValidate = [
        ...ADD_LANDLORD_FIRST_SCREEN_ERROR_FIELD,
        ...ADD_SECOND_SCREEN_ERROR_FIELD
      ];
    } else if (props.extraInfo.type === typeByEntity["bank-accounts"]) {
      fieldsToValidate = ADD_SECOND_SCREEN_ERROR_FIELD;
    }

    fieldsToValidate.forEach(field => {
      if (props.extraInfo.type === typeByEntity.landlords) {
        const isBusiness = props.editMode
          ? Boolean(
              _get(props.selectedPayeeDetail, "international.isBusiness") ??
                _get(props.selectedPayeeDetail, "isBusiness") ??
                _get(props.selectedPayeeDetail, "is_business")
            )
          : props.controls["is_business"]?.value === "true";
        if (isBusiness && (field === "first_name" || field === "last_name")) {
          return;
        }
        if (!isBusiness && field === "company_name") {
          return;
        }
      }

      // Check if field has value in props.controls
      const fieldControl = props.controls[field];
      if (!fieldControl || !isFieldRendered(field)) {
        return;
      }

      if (fieldControl.form && fieldControl.form !== formName) {
        return;
      }

      if (!fieldControl.value) {
        errors.fields[field] = ["REQUIRED_FIELD"];
      }
    });

    if (!_isEmpty(errors.fields)) {
      props.parseServerErrors(errors, formName);
      return false;
    }

    return true;
  };

  const submitSave = () => {
    if (props.extraInfo.type === typeByEntity.employees) {
      if (props.editMode) {
        props.editEmployeeSubmit(
          true,
          {
            cb: () => {
              toggleModalUpdate();
              invokeMessage("success", "Changes saved.");
            }
          },
          setSubmitting
        );
      } else {
        props.addEmployeeSubmit(
          true,
          {
            cb: () => {
              toggleModal();
              invokeMessage("success", "Employee added.");
            }
          },
          setSubmitting
        );
      }
    }

    if (props.extraInfo.type === typeByEntity.suppliers) {
      if (props.editMode) {
        props.editSupplierSubmit(
          true,
          {
            cb: () => {
              toggleModalUpdate();
              invokeMessage("success", "Changes saved.");
            }
          },
          setSubmitting
        );
      } else {
        props.addSupplierSubmit(
          true,
          {
            cb: (err?: any) => {
              if (!err) {
                toggleModal();
                invokeMessage("success", "Supplier added.");
              }
            }
          },
          setSubmitting
        );
      }
    }

    if (props.extraInfo.type === typeByEntity.landlords) {
      if (props.editMode) {
        props.editLandlordSubmit(
          true,
          {
            cb: () => {
              toggleModalUpdate();
              invokeMessage("success", "Changes saved.");
            }
          },
          setSubmitting
        );
      } else {
        props.addLandlordSubmit(
          true,
          {
            cb: (err?: any) => {
              if (!err) {
                toggleModal();
                invokeMessage("success", "Landlord added.");
              }
            }
          },
          setSubmitting
        );
      }
    }

    if (props.extraInfo.type === typeByEntity["bank-accounts"]) {
      if (props.editMode) {
        props.editSelfTransferSubmit(
          true,
          {
            isWallex: true,
            cb: () => {
              toggleModalUpdate();
              invokeMessage("success", "Changes saved.");
            }
          },
          setSubmitting
        );
      } else {
        // props.addSelfTransferSubmit(
        //   true,
        //   {
        //     cb: (err?: any) => {
        //       if (!err) {
        //         toggleModal();
        //         invokeMessage("success", "Bank account added.");
        //       }
        //     }
        //   },
        //   setSubmitting
        // );
      }
    }
  };

  const onSaved = () => {
    const isInternational = _get(props.selectedPayeeDetail, "isInternational");

    if (props.editMode && isInternational) {
      setSubmitting(true);
      setTimeout(() => {
        if (!validateField()) {
          setSubmitting(false);
          return;
        }
        submitSave();
      }, 500);
    } else {
      submitSave();
    }
  };

  const renderContent = () => {
    const commonProps = {
      countryId: props.countryId,
      getAddressByPostalCode: props.getAddressByPostalCode,
      residentialAddress: props.residentialAddress,
      setControl: props.setControl,
      getControl: props.getControl,
      accountCountryCode: props.accountCountryCode,
      editMode: props.editMode,
      editedId: props.extraInfo.payeeId,
      fetchPayees: props.fetchPayees,
      currencyId: props.currencyId,
      paymentCountryCode: props.paymentCountryCode,
      paymentCurrencyCode: props.paymentCurrencyCode,
      isBusinessAccount: props.isBusinessAccount,
      isSupplierPayoutUEN: props.isSupplierPayoutUEN,
      setSupplierPayoutType: props.setSupplierPayoutType,
      features: props.features,
      validateAmount: props.validateAmount,
      checkValidNoteToRecipient: props.checkValidNoteToRecipient,
      selectPayee: props.selectPayee,
      loading: fetching,
      setLoading: setFetching
    };

    const wallexProps = {
      setPaymentCountryCode: props.setPaymentCountryCode,
      setPaymentCurrencyCode: props.setPaymentCurrencyCode
    };

    return (
      <div className={styles.content}>
        {props.extraInfo.type === typeByEntity.landlords && (
          <RecipientLandlordForm
            isHongKongAccount={props.isHongKongAccount}
            isAustraliaAccount={props.isAustraliaAccount}
            {...commonProps}
            {...wallexProps}
          />
        )}
        {props.extraInfo.type === typeByEntity.employees && (
          <RecipientEmployeeForm {...commonProps} />
        )}
        {props.extraInfo.type === typeByEntity.suppliers && (
          <RecipientSupplierForm {...commonProps} {...wallexProps} />
        )}

        {props.extraInfo.type === typeByEntity["bank-accounts"] && (
          <RecipientBankAccountForm
            {...commonProps}
            {...wallexProps}
            entity={"self_transfer"}
          />
        )}
      </div>
    );
  };

  const renderSubmitButton = () => {
    return (
      <Button
        type="primary"
        htmlType="button"
        onClick={onSaved}
        loading={submitting || disabled}
        autoScrollToFirstError={true}
      >
        {`${
          submitting
            ? props.editMode
              ? modalInfo.editModeTitleLoading
              : modalInfo.titleLoading
            : props.editMode
            ? modalInfo.editModeSubmitTitle
            : modalInfo.title
        }`}
      </Button>
    );
  };

  const renderDesktopView = () => {
    return (
      <>
        <div className={styles.wrapper}>
          <DesktopModalLayout
            title={props.editMode ? modalInfo.editModeTitle : modalInfo.title}
            maxWidth450={true}
            onClose={toggleModalUpdate.bind(null, true)}
            onSubmit={onSaved}
            btnSubmit={renderSubmitButton()}
            {...(isAllowedSupplierUENFlow && {
              onBack: onBack
            })}
          >
            {renderContent()}
          </DesktopModalLayout>
        </div>
      </>
    );
  };

  const renderMobileView = () => {
    return (
      <MainLayout
        header={{
          title: props.editMode ? modalInfo.editModeTitle : modalInfo.title,
          hideMenu: true,
          onClose: toggleModalUpdate.bind(null, true)
        }}
        footer={renderSubmitButton()}
      >
        <div className={styles.wrapper}>{renderContent()}</div>
      </MainLayout>
    );
  };

  useEffect(() => {
    props.editPayee(props.extraInfo.payeeId, setFetching);
  }, [props.extraInfo.payeeId]);

  useEffect(() => {
    if (isAllowedSupplierUENFlow && !props.supplierPayoutType) {
      history.push(ROUTES.RECIPIENTS_SUPPLIER_SELECT_TYPE);
    }
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const missingFieldsParam = urlParams.get("missingFields");

    if (missingFieldsParam && !fetching) {
      setTimeout(() => {
        const missingFields = missingFieldsParam.split(",");
        const form = [
          typeByEntity.suppliers,
          typeByEntity.landlords,
          typeByEntity.employees
        ].includes(props.extraInfo.type)
          ? FORM
          : INTERNATIONAL_FORM;

        missingFields.forEach(field => {
          props.setControl({
            errors: [
              {
                code: "REQUIRED_FIELD",
                message: T.transl("REQUIRED_FIELD")
              }
            ],
            form: form,
            name: field
          });
        });

        if (missingFields.length > 0) {
          const element = document.getElementsByName(missingFields[0])[0];
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }
      }, 500);
    }
  }, [fetching]);

  return (
    <>
      <BrowserView>{renderDesktopView()}</BrowserView>
      <MobileView>{renderMobileView()}</MobileView>
    </>
  );
};

export default View;
